import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { getChannelBalances } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  GiftCouponForRedemption,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useGiftCsMemosByOrders } from '@shared/api/giftCsMemo/giftCsMemo.hook';

import {
  formatDateTime,
  formatPhone,
  getChoiceUnitName,
  getItemName,
} from '../utils/redemption.utils';
import { collectCsMemoOrderIds, CsMemoCouponCell } from './csMemo';
import { UndeliverModal } from './UndeliverModal';

interface BulkUpdatePayload {
  /** undefined = 변경 안함 */
  deliveryChannel?: DeliveryChannel | '';
  /** undefined = 변경 안함 */
  priceWhole?: number;
}

interface Props {
  todayDelivered: GiftCouponForRedemption[];
  showTodayDelivered: boolean;
  setShowTodayDelivered: (fn: (prev: boolean) => boolean) => void;
  copyToClipboard: (text: string) => void;
  showToast: (msg: string) => void;
  onBulkUpdate: (couponIds: string[], dto: BulkUpdatePayload) => Promise<void>;
  /**
   * 발송완료 취소 (매체 발송 실패·실수 정정) — 확인 UI는 이 컴포넌트의 UndeliverModal,
   * API 호출·쿼리 갱신·토스트는 호출부 훅 책임. true = 처리됨(모달 닫기 + 선택 해제).
   */
  onUndeliver?: (couponIds: string[], reason: string) => Promise<boolean>;
  /** 헤더 라벨 — 발송내역 조회 페이지에서 "N월 N일 발송 완료" 등으로 교체 */
  title?: string;
  /**
   * 묶음 헤더에 날짜까지 표기 (기본은 시:분:초만).
   * 여러 날짜가 섞이는 번호 검색 결과에서 시각만 보이면 어느 날 발송인지 알 수 없다.
   */
  showGroupDate?: boolean;
}

const KEEP_CHANNEL = '__keep__' as const;
type ChannelEditValue = DeliveryChannel | '' | typeof KEEP_CHANNEL;

const CHANNEL_OPTIONS: Array<{ value: ChannelEditValue; label: string }> = [
  { value: KEEP_CHANNEL, label: '변경 안함' },
  ...DELIVERY_CHANNEL_LIST.map((ch) => ({
    value: ch as ChannelEditValue,
    label: formatDeliveryChannelSelectLabel(ch),
  })),
  { value: '', label: '입력안함' },
];

export const TodayDeliveredTable: FC<Props> = ({
  todayDelivered,
  showTodayDelivered,
  setShowTodayDelivered,
  copyToClipboard,
  showToast,
  onBulkUpdate,
  onUndeliver,
  title = '오늘 발송 완료',
  showGroupDate = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editChannel, setEditChannel] = useState<ChannelEditValue>(KEEP_CHANNEL);
  const [editPriceWhole, setEditPriceWhole] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  // CS 응대 메모 — 펼쳤을 때만 배치 조회 (각 행의 셀이 이 캐시를 읽는다)
  const csMemoOrderIds = useMemo(
    () => collectCsMemoOrderIds(todayDelivered),
    [todayDelivered],
  );
  useGiftCsMemosByOrders(csMemoOrderIds, showTodayDelivered);

  const groupMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const coupon of todayDelivered) {
      const key = formatDateTime(coupon.gifticon?.deliveredAt, true);
      const ids = map.get(key);
      if (ids) {
        ids.push(coupon._id);
      } else {
        map.set(key, [coupon._id]);
      }
    }
    return map;
  }, [todayDelivered]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback(
    (groupKey: string) => {
      const ids = groupMap.get(groupKey);
      if (!ids) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const allSelected = ids.every((id) => next.has(id));
        if (allSelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [groupMap],
  );

  const selectedSummary = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const selected = todayDelivered.filter((c) => selectedIds.has(c._id));

    const itemSet = new Set<string>();
    const unitSet = new Set<string>();
    const priceSet = new Set<number>();
    const channelSet = new Set<string>();
    let hasNoChannel = false;

    for (const c of selected) {
      itemSet.add(getItemName(c) || '-');
      unitSet.add(getChoiceUnitName(c) || '');
      const pw = c.gifticon?.priceWhole;
      if (typeof pw === 'number') priceSet.add(pw);
      const ch = c.gifticon?.deliveryChannel;
      if (ch) channelSet.add(ch);
      else hasNoChannel = true;
    }

    const itemLabel = itemSet.size === 1 ? Array.from(itemSet)[0] : '혼합';
    const unitOnly = Array.from(unitSet).filter((v) => v !== '');
    const unitLabel =
      unitSet.size === 0 ? null : unitSet.size === 1 ? unitOnly[0] ?? null : '혼합';

    const priceLabel =
      priceSet.size === 0
        ? '-'
        : priceSet.size === 1
        ? `${Array.from(priceSet)[0]?.toLocaleString()}원`
        : '혼합';

    const channelTotalKinds = channelSet.size + (hasNoChannel ? 1 : 0);
    let channelLabel: string;
    let channelBadgeClass: string | null = null;
    if (channelTotalKinds === 0) {
      channelLabel = '입력안함';
    } else if (channelTotalKinds === 1) {
      if (hasNoChannel) {
        channelLabel = '입력안함';
      } else {
        const ch = Array.from(channelSet)[0] as DeliveryChannel;
        const cfg = DELIVERY_CHANNEL_CONFIG[ch];
        channelLabel = cfg?.label ?? ch;
        channelBadgeClass = cfg?.badgeClass ?? null;
      }
    } else {
      channelLabel = '혼합';
    }

    return {
      itemLabel,
      itemMixed: itemSet.size > 1,
      unitLabel,
      unitMixed: unitSet.size > 1,
      priceLabel,
      priceMixed: priceSet.size > 1,
      priceValue: priceSet.size === 1 ? Array.from(priceSet)[0] : undefined,
      channelLabel,
      channelMixed: channelTotalKinds > 1,
      channelBadgeClass,
      channelValue:
        channelTotalKinds === 1
          ? hasNoChannel
            ? ('' as DeliveryChannel | '')
            : (Array.from(channelSet)[0] as DeliveryChannel)
          : undefined,
    };
  }, [selectedIds, todayDelivered]);

  const trimmedPriceWhole = editPriceWhole.trim();
  const parsedPriceWhole =
    trimmedPriceWhole === '' ? undefined : Number(trimmedPriceWhole.replace(/,/g, ''));
  const isPriceWholeValid =
    parsedPriceWhole === undefined ||
    (Number.isFinite(parsedPriceWhole) && parsedPriceWhole >= 0);
  const isChannelDirty = editChannel !== KEEP_CHANNEL;
  const isPriceWholeDirty = parsedPriceWhole !== undefined;
  const hasChanges = isChannelDirty || isPriceWholeDirty;

  const editSummary = (() => {
    const parts: string[] = [];
    if (isPriceWholeDirty) parts.push('도매가');
    if (isChannelDirty) parts.push('매체');
    return parts.length > 0 ? `수정 (${parts.join(', ')})` : '수정';
  })();

  /**
   * 수정 적용 시 원장 변화 미리보기 — 백엔드 교정 재기장(recordChannelCorrectionSafe)과
   * 같은 규칙: 같은 채널 = 차액 −(신−구), 채널 이동 = 구채널 회수(+구도매가) + 신채널 기장(−신도매가).
   * 실제 반영은 기장분(channelDeduct 有)만이라 예상치. null = 미리보기 비노출.
   */
  const ledgerPreview = useMemo(() => {
    if (selectedIds.size === 0 || !hasChanges || !isPriceWholeValid) return null;
    const isValidChannel = (ch: unknown): ch is DeliveryChannel =>
      DELIVERY_CHANNEL_LIST.includes(ch as DeliveryChannel);
    const deltas = new Map<DeliveryChannel, number>();
    const add = (ch: DeliveryChannel, amount: number) =>
      deltas.set(ch, (deltas.get(ch) ?? 0) + amount);

    for (const coupon of todayDelivered) {
      if (!selectedIds.has(coupon._id)) continue;
      const oldCh = coupon.gifticon?.deliveryChannel;
      const oldPw = coupon.gifticon?.priceWhole;
      const newCh = isChannelDirty ? editChannel : oldCh;
      const newPw = isPriceWholeDirty ? parsedPriceWhole : oldPw;
      const oldValid = isValidChannel(oldCh) && typeof oldPw === 'number';
      const newValid =
        isValidChannel(newCh) && typeof newPw === 'number' && newPw >= 0;

      if (oldValid && newValid && oldCh === newCh) {
        add(oldCh, -(newPw - oldPw));
      } else {
        if (oldValid) add(oldCh, oldPw);
        if (newValid) add(newCh, -newPw);
      }
    }
    return Array.from(deltas.entries())
      .map(([channel, amount]) => ({ channel, amount }))
      .filter((d) => d.amount !== 0);
  }, [
    selectedIds,
    hasChanges,
    isPriceWholeValid,
    todayDelivered,
    isChannelDirty,
    editChannel,
    isPriceWholeDirty,
    parsedPriceWhole,
  ]);

  // 원장 잔액 — 미리보기가 보일 때마다 새로 조회 (구서버 404 등이면 잔액 표기만 생략)
  const [channelBalances, setChannelBalances] = useState<
    ChannelBalanceSummary[] | null
  >(null);
  const showLedgerPreview = ledgerPreview !== null;
  useEffect(() => {
    if (!showLedgerPreview) return;
    let alive = true;
    getChannelBalances()
      .then((b) => {
        if (alive) setChannelBalances(b);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [showLedgerPreview]);

  const handleCopySelectedPhones = useCallback(() => {
    const ids = selectedIds;
    if (ids.size === 0) return;
    const phones = todayDelivered
      .filter((c) => ids.has(c._id))
      .map((c) => c.recipient?.phoneNumber)
      .filter((p): p is string => !!p)
      .map((p) => p.replace(/[^0-9]/g, ''))
      .filter((p) => p.length > 0);
    if (phones.length === 0) {
      showToast('복사할 번호가 없습니다.');
      return;
    }
    copyToClipboard(phones.join('\n'));
  }, [selectedIds, todayDelivered, copyToClipboard, showToast]);

  const handleApply = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!hasChanges) return;
    if (!isPriceWholeValid) return;

    const dto: BulkUpdatePayload = {};
    if (isChannelDirty) dto.deliveryChannel = editChannel as DeliveryChannel | '';
    if (isPriceWholeDirty) dto.priceWhole = parsedPriceWhole;

    setIsUpdating(true);
    try {
      await onBulkUpdate(ids, dto);
      setSelectedIds(new Set());
      setEditChannel(KEEP_CHANNEL);
      setEditPriceWhole('');
    } finally {
      setIsUpdating(false);
    }
  };

  // 발송취소 모달 대상 — 열 때의 선택 스냅샷 (null = 닫힘)
  const [undeliverTargets, setUndeliverTargets] = useState<
    GiftCouponForRedemption[] | null
  >(null);

  const openUndeliverModal = () => {
    if (selectedIds.size === 0) return;
    setUndeliverTargets(todayDelivered.filter((c) => selectedIds.has(c._id)));
  };

  const handleConfirmUndeliver = async (reason: string) => {
    if (!onUndeliver || !undeliverTargets) return false;
    const done = await onUndeliver(
      undeliverTargets.map((c) => c._id),
      reason,
    );
    if (done) {
      setUndeliverTargets(null);
      setSelectedIds(new Set());
      setEditChannel(KEEP_CHANNEL);
      setEditPriceWhole('');
    }
    return done;
  };

  const channelCounts = useMemo(() => {
    const counts = new Map<
      string,
      { label: string; badgeClass: string; count: number; wholeSum: number }
    >();
    for (const coupon of todayDelivered) {
      const ch = coupon.gifticon?.deliveryChannel;
      if (!ch) continue;
      const pw = coupon.gifticon?.priceWhole ?? 0;
      const existing = counts.get(ch);
      if (existing) {
        existing.count++;
        existing.wholeSum += pw;
      } else {
        const config = DELIVERY_CHANNEL_CONFIG[ch];
        counts.set(ch, {
          label: config.label,
          badgeClass: config.badgeClass,
          count: 1,
          wholeSum: pw,
        });
      }
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [todayDelivered]);

  /** 발송시각 그룹별 건수·도매가(개별/합산)·옵션명·매체 (마감 대조용) */
  const groupSummaryMap = useMemo(() => {
    const map = new Map<
      string,
      {
        count: number;
        wholeSum: number;
        nameSet: Set<string>;
        channelSet: Set<DeliveryChannel>;
      }
    >();
    for (const coupon of todayDelivered) {
      const key = formatDateTime(coupon.gifticon?.deliveredAt, true);
      const pw = coupon.gifticon?.priceWhole ?? 0;
      let g = map.get(key);
      if (!g) {
        g = {
          count: 0,
          wholeSum: 0,
          nameSet: new Set(),
          channelSet: new Set(),
        };
        map.set(key, g);
      }
      g.count++;
      g.wholeSum += pw;
      g.nameSet.add(getChoiceUnitName(coupon) || getItemName(coupon) || '-');
      const ch = coupon.gifticon?.deliveryChannel;
      if (ch) g.channelSet.add(ch);
    }
    return map;
  }, [todayDelivered]);

  if (todayDelivered.length === 0) return null;

  return (
    <div className="px-4 pb-2">
      <button
        onClick={() => setShowTodayDelivered((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border-2 border-green-500 bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-green-800">
            {title} ({todayDelivered.length}건)
          </span>
          {channelCounts.map((ch) => (
            <span
              key={ch.label}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ch.badgeClass}`}
            >
              {ch.label} {ch.count}건
              <span className="font-bold">{ch.wholeSum.toLocaleString()}원</span>
            </span>
          ))}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-green-600 transition-transform ${
            showTodayDelivered ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {showTodayDelivered && (
        <>
          {/* 선택 항목 요약 */}
          {selectedSummary && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-indigo-100/70 px-4 py-2 text-xs text-indigo-900">
              <span className="font-semibold">선택 {selectedIds.size}건 현재값:</span>
              <span>
                <span className="text-indigo-500">상품</span>{' '}
                <span
                  className={
                    selectedSummary.itemMixed ? 'font-bold text-rose-600' : 'font-medium'
                  }
                >
                  {selectedSummary.itemLabel}
                </span>
                {selectedSummary.unitLabel && (
                  <>
                    {' '}
                    <span
                      className={
                        selectedSummary.unitMixed
                          ? 'font-bold text-rose-600'
                          : 'text-indigo-700'
                      }
                    >
                      ({selectedSummary.unitLabel})
                    </span>
                  </>
                )}
              </span>
              <span>
                <span className="text-indigo-500">도매가</span>{' '}
                {selectedSummary.priceValue !== undefined ? (
                  <button
                    type="button"
                    onClick={() => setEditPriceWhole(String(selectedSummary.priceValue))}
                    title="현재 값으로 입력"
                    className="font-medium text-indigo-900 underline decoration-dotted hover:text-indigo-700"
                  >
                    {selectedSummary.priceLabel}
                  </button>
                ) : (
                  <span
                    className={
                      selectedSummary.priceMixed
                        ? 'font-bold text-rose-600'
                        : 'font-medium'
                    }
                  >
                    {selectedSummary.priceLabel}
                  </span>
                )}
              </span>
              <span>
                <span className="text-indigo-500">매체</span>{' '}
                {selectedSummary.channelValue !== undefined ? (
                  <button
                    type="button"
                    onClick={() =>
                      setEditChannel(selectedSummary.channelValue as ChannelEditValue)
                    }
                    title="현재 값으로 입력"
                    className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold hover:opacity-80 ${
                      selectedSummary.channelBadgeClass ?? 'border-gray-300 text-gray-600'
                    }`}
                  >
                    {selectedSummary.channelLabel}
                  </button>
                ) : (
                  <span
                    className={
                      selectedSummary.channelMixed
                        ? 'font-bold text-rose-600'
                        : 'font-medium'
                    }
                  >
                    {selectedSummary.channelLabel}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* 선택 시 수정 바 (도매가/매체) */}
          {selectedIds.size > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-indigo-50 px-4 py-2">
              <span className="text-xs font-medium text-indigo-700">
                {selectedIds.size}건 선택
              </span>
              <label className="flex items-center gap-1 text-xs text-indigo-700">
                <span>도매가</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={editPriceWhole}
                  onChange={(e) => setEditPriceWhole(e.target.value)}
                  disabled={isUpdating}
                  placeholder="변경 안함"
                  className={`w-24 rounded border px-2 py-1 text-xs focus:outline-none ${
                    isPriceWholeValid
                      ? 'border-indigo-300 focus:border-indigo-500'
                      : 'border-red-400 focus:border-red-500'
                  }`}
                />
                <span>원</span>
              </label>
              <label className="flex items-center gap-1 text-xs text-indigo-700">
                <span>매체</span>
                <select
                  value={editChannel}
                  onChange={(e) => setEditChannel(e.target.value as ChannelEditValue)}
                  disabled={isUpdating}
                  className="rounded border border-indigo-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
                >
                  {CHANNEL_OPTIONS.map((opt) => (
                    <option
                      key={opt.value === '' ? '__none__' : opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={handleApply}
                disabled={isUpdating || !hasChanges || !isPriceWholeValid}
                className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isUpdating ? '처리중...' : editSummary}
              </button>
              <button
                onClick={handleCopySelectedPhones}
                disabled={isUpdating}
                title={`선택된 수신자 번호 복사 (${selectedIds.size}건)`}
                className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                번호 복사 ({selectedIds.size})
              </button>
              {onUndeliver && (
                <button
                  onClick={openUndeliverModal}
                  disabled={isUpdating}
                  title="발송대기 복귀 + 원장 환급"
                  className="rounded border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  발송취소 ({selectedIds.size})
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedIds(new Set());
                  setEditChannel(KEEP_CHANNEL);
                  setEditPriceWhole('');
                }}
                disabled={isUpdating}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                선택 해제
              </button>

              {/* 원장 변화 미리보기 — 수정 적용 시 채널별 ± 및 잔액 대조 */}
              {ledgerPreview && (
                <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 border-t border-indigo-200 pt-1.5 text-xs">
                  <span className="font-semibold text-gray-700">원장</span>
                  {ledgerPreview.length === 0 ? (
                    <span className="text-gray-600">변화 없음</span>
                  ) : (
                    ledgerPreview.map(({ channel, amount }) => {
                      const cfg = DELIVERY_CHANNEL_CONFIG[channel];
                      const balance =
                        channelBalances?.find((b) => b.channel === channel) ?? null;
                      return (
                        <span
                          key={channel}
                          className="inline-flex items-center gap-1.5"
                        >
                          <span className="font-medium text-gray-800">
                            {cfg?.label ?? channel}
                          </span>
                          <span
                            className={`font-bold tabular-nums ${
                              amount < 0 ? 'text-rose-600' : 'text-gray-900'
                            }`}
                          >
                            {amount > 0 ? '+' : ''}
                            {amount.toLocaleString()}
                          </span>
                          {balance && (
                            <span className="tabular-nums text-gray-600">
                              잔액 {balance.balance.toLocaleString()}→
                              <span className="font-semibold text-gray-900">
                                {(balance.balance + amount).toLocaleString()}
                              </span>
                            </span>
                          )}
                        </span>
                      );
                    })
                  )}
                  <span className="text-gray-500">기장분 기준</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-2 overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full">
              <thead className="bg-green-50">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    쿠폰번호
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    _id
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    상품
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    메모
                  </th>
                  <th
                    className="px-4 py-2 text-left text-xs font-medium text-green-700"
                    title="CS 응대 메모 (클릭 시 메모장)"
                  >
                    CS메모
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    확인번호
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    수신자
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-green-700">
                    도매가
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    신청시각
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    발송시각
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    매체
                  </th>
                </tr>
              </thead>
              <tbody>
                {todayDelivered.map((coupon, idx) => {
                  const curKey = formatDateTime(coupon.gifticon?.deliveredAt, true);
                  const prevKey =
                    idx > 0
                      ? formatDateTime(
                          todayDelivered[idx - 1]?.gifticon?.deliveredAt,
                          true,
                        )
                      : null;
                  const isNewGroup = idx === 0 || curKey !== prevKey;
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const groupIds = groupMap.get(curKey) ?? [];
                  const isGroupAllSelected =
                    groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
                  const groupSummary = groupSummaryMap.get(curKey);

                  const channelConfig = coupon.gifticon?.deliveryChannel
                    ? DELIVERY_CHANNEL_CONFIG[coupon.gifticon.deliveryChannel]
                    : null;

                  return (
                    <Fragment key={coupon._id}>
                      {isNewGroup && groupSummary && (
                        <tr
                          className={`bg-green-50/70 ${
                            idx > 0 ? 'border-t-2 border-green-200' : ''
                          }`}
                        >
                          <td
                            colSpan={12}
                            className="px-4 py-1.5 text-xs text-green-800"
                          >
                            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                              <span className="font-semibold">
                                {showGroupDate ? curKey : curKey.split(' ')[1] ?? curKey}
                              </span>
                              <span className="font-medium text-gray-700">
                                {groupSummary.nameSet.size === 1
                                  ? Array.from(groupSummary.nameSet)[0]
                                  : '혼합'}
                              </span>
                              {Array.from(groupSummary.channelSet).map((ch) => {
                                const cfg = DELIVERY_CHANNEL_CONFIG[ch];
                                return (
                                  <span
                                    key={ch}
                                    className={`inline-flex shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cfg.badgeClass}`}
                                  >
                                    {cfg.label}
                                  </span>
                                );
                              })}
                              <span className="text-green-300">·</span>
                              <span>{groupSummary.count}건</span>
                              <span className="text-green-300">·</span>
                              <span className="font-semibold">
                                도매가합산 {groupSummary.wholeSum.toLocaleString()}원
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr
                        className={`hover:bg-gray-50 ${
                          selectedIds.has(coupon._id) ? 'bg-indigo-50/40' : ''
                        }`}
                      >
                      <td className="px-2 py-2 text-center">
                        {isNewGroup ? (
                          <input
                            type="checkbox"
                            checked={isGroupAllSelected}
                            onChange={() => toggleGroup(curKey)}
                            title={`묶음 일괄선택 (${groupIds.length}건)`}
                            className="h-3.5 w-3.5 cursor-pointer rounded border-indigo-400 text-indigo-600 accent-indigo-600"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(coupon._id)}
                            onChange={() => toggleOne(coupon._id)}
                            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-indigo-600 accent-indigo-600"
                          />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700">
                        {coupon.fullCode || '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <button
                          onClick={() => copyToClipboard(coupon._id)}
                          className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                          title="클릭하여 _id 복사"
                        >
                          {coupon._id.slice(-6)}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-900">
                        {getItemName(coupon)}
                        {choiceUnitName && (
                          <span className="ml-1 text-indigo-500">({choiceUnitName})</span>
                        )}
                      </td>
                      <td className="max-w-[120px] px-4 py-2">
                        {coupon.memo ? (
                          <p
                            className="truncate text-xs text-gray-600"
                            title={coupon.memo}
                          >
                            {coupon.memo}
                          </p>
                        ) : (
                          <span className="text-[10px] text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <CsMemoCouponCell coupon={coupon} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-amber-600">
                        {coupon.additionalRecipientPhone ? (
                          formatPhone(coupon.additionalRecipientPhone)
                        ) : (
                          <span className="text-[10px] text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-700">
                        {formatPhone(coupon.recipient?.phoneNumber)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-indigo-600">
                        {coupon.gifticon?.priceWhole != null
                          ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                        {formatDateTime(coupon.gifticon?.deliveredAt, true)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs">
                        {channelConfig ? (
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${channelConfig.badgeClass}`}
                          >
                            {channelConfig.label}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {undeliverTargets && onUndeliver && (
        <UndeliverModal
          coupons={undeliverTargets}
          onClose={() => setUndeliverTargets(null)}
          onConfirm={handleConfirmUndeliver}
        />
      )}
    </div>
  );
};
